import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type Update,
} from '@reduxjs/toolkit';
import type {
  Segment,
  SegmentCategory,
  SegmentListResponse,
} from '@interfaces/segments/domain';

export const segmentsAdapter = createEntityAdapter<Segment, string>({
  selectId: (s) => s.code,
  sortComparer: (a, b) => a.sortOrder - b.sortOrder,
});
export const segmentCategoriesAdapter = createEntityAdapter<SegmentCategory, string>({
  selectId: (c) => c.code,
  sortComparer: (a, b) => a.sortOrder - b.sortOrder,
});

export interface SegmentsState {
  segments: EntityState<Segment, string>;
  categories: EntityState<SegmentCategory, string>;
  initialized: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: SegmentsState = {
  segments: segmentsAdapter.getInitialState(),
  categories: segmentCategoriesAdapter.getInitialState(),
  initialized: false,
  loading: false,
  error: null,
};

const segmentsSlice = createSlice({
  name: 'segments',
  initialState,
  reducers: {
    setSegmentsCatalog: (state, action: PayloadAction<SegmentListResponse>) => {
      segmentsAdapter.setAll(state.segments, action.payload.segments);
      segmentCategoriesAdapter.setAll(state.categories, action.payload.categories);
      state.initialized = true;
      state.error = null;
      state.loading = false;
    },
    optimisticInsertSegment: (state, action: PayloadAction<Segment>) => {
      segmentsAdapter.upsertOne(state.segments, action.payload);
    },
    optimisticUpdateSegment: (state, action: PayloadAction<Update<Segment, string>>) => {
      segmentsAdapter.updateOne(state.segments, action.payload);
    },
    optimisticRemoveSegment: (state, action: PayloadAction<string>) => {
      segmentsAdapter.removeOne(state.segments, action.payload);
    },
    optimisticInsertCategory: (state, action: PayloadAction<SegmentCategory>) => {
      segmentCategoriesAdapter.upsertOne(state.categories, action.payload);
    },
    optimisticUpdateCategory: (state, action: PayloadAction<Update<SegmentCategory, string>>) => {
      segmentCategoriesAdapter.updateOne(state.categories, action.payload);
    },
    optimisticRemoveCategory: (state, action: PayloadAction<string>) => {
      segmentCategoriesAdapter.removeOne(state.categories, action.payload);
    },
    rollbackToServerSnapshot: (state, action: PayloadAction<SegmentListResponse>) => {
      segmentsAdapter.setAll(state.segments, action.payload.segments);
      segmentCategoriesAdapter.setAll(state.categories, action.payload.categories);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setSegmentsCatalog,
  optimisticInsertSegment,
  optimisticUpdateSegment,
  optimisticRemoveSegment,
  optimisticInsertCategory,
  optimisticUpdateCategory,
  optimisticRemoveCategory,
  rollbackToServerSnapshot,
  setLoading,
  setError,
} = segmentsSlice.actions;
export default segmentsSlice.reducer;

export type SegmentsAdapter = EntityState<Segment, string>;
export type SegmentCategoriesAdapter = EntityState<SegmentCategory, string>;
export type SegmentsCategoriesAdapterType = {
  segments: EntityState<Segment, string>;
  categories: EntityState<SegmentCategory, string>;
};
